var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/lib/components/auth-guard/index.tsx
import { usePermissions } from "@oppy/omniv2-utils";
import { Fragment } from "react";
import { Navigate } from "react-router-dom";

// src/lib/providers/user-provider/index.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";

// src/lib/utils/user-manager.ts
var UserManager = class {
  constructor() {
    __publicField(this, "currentUser", null);
    __publicField(this, "listeners", /* @__PURE__ */ new Set());
  }
  getCurrentUser() {
    return this.currentUser;
  }
  setCurrentUser(user) {
    this.currentUser = user;
    this.notifyListeners();
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener(this.currentUser);
      } catch (error) {
        console.error("Error in user update listener:", error);
      }
    });
  }
  clear() {
    this.setCurrentUser(null);
  }
};
var userManager = new UserManager();

// src/lib/providers/user-provider/index.tsx
import { jsx } from "react/jsx-runtime";
var UserContext = createContext(null);
function UserProvider({
  children,
  initialUser = null
}) {
  const [currentUser, setCurrentUser] = useState(
    initialUser || userManager.getCurrentUser()
  );
  const [isLoading, setIsLoading] = useState(!initialUser);
  useEffect(() => {
    const unsubscribe = userManager.subscribe((user) => {
      setCurrentUser(user);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (initialUser) {
      userManager.setCurrentUser(initialUser);
    }
  }, [initialUser]);
  const isAuthenticated = currentUser !== null;
  const login = useCallback((userData) => {
    userManager.setCurrentUser(userData);
  }, []);
  const logout = useCallback(() => {
    userManager.clear();
  }, []);
  const contextValue = {
    currentUser,
    isAuthenticated,
    isLoading,
    login,
    logout
  };
  return /* @__PURE__ */ jsx(UserContext.Provider, { value: contextValue, children });
}
function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
function useCurrentUser() {
  const { currentUser } = useUser();
  return currentUser;
}
var user_provider_default = UserProvider;

// src/lib/components/auth-guard/index.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var AUTH_MODE = {
  REDIRECT: "redirect",
  HIDE: "hide",
  FALLBACK: "fallback"
};
var AUTH_STATE = {
  LOADING: "loading",
  AUTHORIZED: "authorized",
  UNAUTHORIZED: "unauthorized"
};
function AuthGuard({
  children,
  resource,
  permission,
  redirectTo = "/forbidden",
  fallback = null,
  mode = AUTH_MODE.REDIRECT
}) {
  const { currentUser, isLoading } = useUser();
  const { hasPermission } = usePermissions(currentUser ?? void 0);
  const getAuthState = () => {
    if (isLoading) return AUTH_STATE.LOADING;
    return hasPermission(resource, permission) ? AUTH_STATE.AUTHORIZED : AUTH_STATE.UNAUTHORIZED;
  };
  const renderByState = (state) => {
    const stateHandlers = {
      [AUTH_STATE.LOADING]: () => mode === AUTH_MODE.FALLBACK ? fallback : null,
      [AUTH_STATE.AUTHORIZED]: () => children,
      [AUTH_STATE.UNAUTHORIZED]: () => {
        const unauthorizedHandlers = {
          [AUTH_MODE.REDIRECT]: () => /* @__PURE__ */ jsx2(Navigate, { to: redirectTo, replace: true }),
          [AUTH_MODE.HIDE]: () => null,
          [AUTH_MODE.FALLBACK]: () => fallback
        };
        return unauthorizedHandlers[mode]();
      }
    };
    return stateHandlers[state]();
  };
  return /* @__PURE__ */ jsx2(Fragment, { children: renderByState(getAuthState()) });
}

// src/lib/components/component-guard/index.tsx
import { usePermissions as usePermissions2 } from "@oppy/omniv2-utils";
import { Fragment as Fragment2 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
function ComponentGuard({
  children,
  resource,
  permission,
  fallback = null,
  showFallback = false
}) {
  const { currentUser, isLoading } = useUser();
  const { hasPermission } = usePermissions2(currentUser ?? void 0);
  if (isLoading || !hasPermission(resource, permission)) {
    return showFallback ? /* @__PURE__ */ jsx3(Fragment2, { children: fallback }) : null;
  }
  return /* @__PURE__ */ jsx3(Fragment2, { children });
}

// src/lib/components/hydrate-atoms/hydrate-atoms.tsx
import { useHydrateAtoms } from "jotai/utils";
function HydrateAtoms({ atomValues, children }) {
  useHydrateAtoms(new Map(atomValues));
  return children;
}

// src/lib/components/route-guard/index.tsx
import { usePermissions as usePermissions3 } from "@oppy/omniv2-utils";
import { Fragment as Fragment3 } from "react";
import { Navigate as Navigate2 } from "react-router-dom";
import { jsx as jsx4 } from "react/jsx-runtime";
function RouteGuard({
  children,
  resource,
  permission,
  fallbackPath = "/forbidden"
}) {
  const { currentUser, isLoading } = useUser();
  const { hasPermission } = usePermissions3(currentUser ?? void 0);
  if (isLoading) {
    return null;
  }
  const isAuthorized = hasPermission(resource, permission);
  if (!isAuthorized) {
    return /* @__PURE__ */ jsx4(Navigate2, { to: fallbackPath, replace: true });
  }
  return /* @__PURE__ */ jsx4(Fragment3, { children });
}

// src/lib/components/protected-route/index.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function ProtectedRoute(props) {
  return /* @__PURE__ */ jsx5(RouteGuard, { ...props });
}

// src/lib/core/services/create-client.ts
import axios from "axios";
function createClient(options) {
  return axios.create(options);
}

// src/lib/core/services/omniapi-client.constants.ts
var COMMON_CLIENT_CONFIG = {
  withCredentials: true,
  baseURL: import.meta.env.VITE_OMNIAPI_ENDPOINT
};
var OLD_OMNI_API_CLIENT_CONFIG = {
  withCredentials: true,
  baseURL: import.meta.env.VITE_OMNIAPI_ENDPOINT_OLD
};

// src/lib/core/services/refresh-client.ts
var refreshClient = createClient(COMMON_CLIENT_CONFIG);

// src/lib/utils/constants.ts
var HTTP_CODE_UNAUTHORIZED = 401;
var API_REFRESH_PATH = "auth/login/user/refresh";

// src/lib/utils/jwt-utils.ts
var base64UrlDecode = (str) => {
  const normalizedBase64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const paddingLength = (4 - normalizedBase64.length % 4) % 4;
  const paddedBase64 = normalizedBase64 + "=".repeat(paddingLength);
  return atob(paddedBase64);
};
var decodeJWTPayload = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }
    const payloadBase64 = parts[1];
    const payloadJson = base64UrlDecode(payloadBase64);
    const payload = JSON.parse(payloadJson);
    return payload;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};
function extractJWTFromSetCookie(setCookieHeader) {
  const cookieHeaders = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
  for (const header of cookieHeaders) {
    const accessTokenMatch = header.match(/accessToken=([^;]+)/);
    if (accessTokenMatch) {
      const token = accessTokenMatch[1];
      return token;
    }
  }
  return null;
}
function decodeUserFromJWT(token) {
  const payload = decodeJWTPayload(token);
  if (!payload) {
    console.error("\u274C Failed to decode JWT payload");
    return null;
  }
  const roleMapping = {
    Oppy: "oppy",
    Client: "client",
    Admin: "admin"
  };
  const role = roleMapping[payload.role] || payload.role.toLowerCase();
  const user = {
    id: payload.nameid,
    role,
    email: void 0,
    name: void 0,
    organizationType: void 0
  };
  return user;
}
function extractUserFromSetCookie(setCookieHeader) {
  const token = extractJWTFromSetCookie(setCookieHeader);
  if (!token) {
    return null;
  }
  const user = decodeUserFromJWT(token);
  return user;
}

// src/lib/utils/auth-initializer.ts
async function initializeAuth() {
  try {
    const refreshResponse = await refreshClient.post(API_REFRESH_PATH);
    if (refreshResponse.data?.user) {
      const userData = refreshResponse.data.user;
      const normalizedRole = userData.role?.toLowerCase();
      const user = {
        id: userData.id || userData.nameid,
        role: normalizedRole,
        email: userData.email,
        name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || void 0,
        organizationType: userData.organizationType
      };
      userManager.setCurrentUser(user);
      return;
    }
    if (refreshResponse.data?.accessToken) {
      const user = decodeUserFromJWT(refreshResponse.data.accessToken);
      if (user) {
        userManager.setCurrentUser(user);
        return;
      }
    }
    userManager.clear();
  } catch {
    userManager.clear();
  }
}

// src/lib/utils/create-permission-helper.ts
import {
  usePermissions as usePermissions4
} from "@oppy/omniv2-utils";
function useAuthPermissions() {
  const currentUser = useCurrentUser();
  const permissions = usePermissions4(currentUser ?? void 0);
  const hasAccess = (resource, permission = "access") => permissions.hasPermission(resource, permission);
  const canAccessResource = (resource) => hasAccess(resource, "access");
  return {
    currentUser,
    isAuthenticated: !!currentUser,
    hasAccess,
    canAccessResource,
    ...permissions
  };
}

// src/lib/utils/enhance-api-error.ts
function enhanceApiError(error, showOnError) {
  if (isHttpError(error) && !showOnError)
    return new SilentHttpError(error.status);
  return error;
}

// src/lib/utils/has-required-number-props.ts
function hasRequiredNumberProps(object, key) {
  return key in object && typeof object[key] === "number";
}

// src/lib/utils/has-required-string-props.ts
function hasRequiredStringProps(object, key) {
  return key in object && typeof object[key] === "string";
}

// src/lib/utils/token-manager.ts
var TokenManager = class {
  constructor() {
    __publicField(this, "tokenGetter", null);
  }
  setTokenGetter(getter) {
    this.tokenGetter = getter;
  }
  async getAccessToken() {
    if (!this.tokenGetter) {
      return null;
    }
    try {
      return await this.tokenGetter();
    } catch {
      return null;
    }
  }
  clear() {
    this.tokenGetter = null;
  }
};
var tokenManager = new TokenManager();

// src/lib/core/errors/api-error.ts
function isApiError(error) {
  return !!error && typeof error === "object" && ["type", "title", "status", "detail", "trace-id", "instance"].every(
    (key) => hasRequiredStringProps(error, key)
  );
}
var ApiError = class extends Error {
  constructor(detail, traceId) {
    super(`${detail} (trace: ${traceId})`);
  }
};

// src/lib/core/errors/api-refresh-error.ts
var ApiRefreshError = class extends Error {
  constructor(endpoint) {
    super(`Could not refresh from endpoint: ${endpoint}`);
  }
};

// src/lib/core/errors/http-error.ts
function isHttpError(error) {
  return !!error && typeof error === "object" && ["status"].every(
    (key) => hasRequiredNumberProps(error, key)
  );
}
var SilentHttpError = class extends Error {
  constructor(status, message) {
    super(message);
    __publicField(this, "status");
    this.status = status;
  }
};

// src/lib/core/services/interceptors/access-token-interceptor.ts
import axios2 from "axios";
function accessTokenInterceptor(instance) {
  instance.interceptors.request.use(
    async (config) => {
      const token = await tokenManager.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { response } = error;
      const originalRequest = error.config ?? {};
      if (response?.status === 403) {
        const message = error.message || "";
        if (message.includes("Missing Refresh Token") || message.includes("invalid refresh token")) {
          throw new ApiRefreshError(originalRequest?.url);
        }
      }
      if (!originalRequest || !response || response.status !== HTTP_CODE_UNAUTHORIZED)
        throw error;
      if (originalRequest?._retry) {
        throw new ApiRefreshError(originalRequest?.url);
      }
      originalRequest._retry = true;
      try {
        const token = await tokenManager.getAccessToken();
        if (!token) {
          throw new ApiRefreshError(originalRequest?.url);
        }
        originalRequest.headers.Authorization = `Bearer ${token}`;
        const response2 = await axios2(originalRequest);
        return response2;
      } catch {
        throw new ApiRefreshError(originalRequest?.url);
      }
    }
  );
}

// src/lib/core/services/interceptors/response-interceptor.ts
function responseInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response.data,
    (error) => {
      if (isApiError(error)) {
        throw new ApiError(error.detail, error["trace-id"]);
      }
      throw error;
    }
  );
}

// src/lib/core/services/omniapi-client.ts
var omniApiClient = createClient(COMMON_CLIENT_CONFIG);
accessTokenInterceptor(omniApiClient);
responseInterceptor(omniApiClient);

// src/lib/core/services/omniapi-client-old.ts
var omniApiClientOld = createClient(OLD_OMNI_API_CLIENT_CONFIG);
accessTokenInterceptor(omniApiClientOld);
responseInterceptor(omniApiClientOld);

// src/types/campaigns.ts
var CampaignStatus = /* @__PURE__ */ ((CampaignStatus2) => {
  CampaignStatus2["CREATED"] = "CREATED";
  CampaignStatus2["RUNNING"] = "RUNNING";
  CampaignStatus2["PAUSED"] = "PAUSED";
  CampaignStatus2["STOPPED"] = "STOPPED";
  CampaignStatus2["COMPLETED"] = "COMPLETED";
  CampaignStatus2["ARCHIVED"] = "ARCHIVED";
  CampaignStatus2["WAITING"] = "WAITING";
  CampaignStatus2["CANCELLED"] = "CANCELLED";
  return CampaignStatus2;
})(CampaignStatus || {});
export {
  API_REFRESH_PATH,
  ApiError,
  ApiRefreshError,
  AuthGuard,
  CampaignStatus,
  ComponentGuard,
  HTTP_CODE_UNAUTHORIZED,
  HydrateAtoms,
  ProtectedRoute,
  RouteGuard,
  SilentHttpError,
  user_provider_default as UserProvider,
  accessTokenInterceptor,
  createClient,
  decodeUserFromJWT,
  enhanceApiError,
  extractJWTFromSetCookie,
  extractUserFromSetCookie,
  hasRequiredNumberProps,
  hasRequiredStringProps,
  initializeAuth,
  isApiError,
  isHttpError,
  omniApiClient,
  omniApiClientOld,
  responseInterceptor,
  tokenManager,
  useAuthPermissions,
  useCurrentUser,
  useUser,
  userManager
};
